import { Image } from '@rocco/components/image';
import { Skeleton } from '@rocco/components/skeleton';
import { cn } from '@rocco/utils/cn';

export interface GateSubjectHeaderProps {
    /**
     * The item being enquired about. On a product with variants this is the
     * selected variant's own photograph, not the product default — the reader
     * picked a finish or a size, and the header is where they check they are
     * asking about the one they picked.
     */
    image?: string;
    /** The professional's mark. Shown beside the item, as the legacy header did. */
    logo?: string;
    /**
     * Brand colour behind the logo. Logos are drawn for a specific ground and
     * are shown whole on it rather than cropped to fill.
     */
    logoBackground?: string;
    /** What the reader is acting on — the product, project or article. */
    title?: string;
    /** Who it belongs to, and who the enquiry reaches. */
    professionalTitle: string;
    /**
     * The professional's details are still resolving, so hold a shimmer rather
     * than an empty tile — the same signal the legacy modal's header used.
     */
    logoPending?: boolean;
    /**
     * Hold the header against the top of the sheet while the form scrolls under
     * it.
     *
     * The subject is the answer to "what am I filling this in for", and on a
     * long form it is the first thing to leave the screen — exactly when the
     * reader is deepest in and most likely to want it. `-mx-6 px-6` cancels the
     * sheet body's own padding so the opaque strip reaches both edges; keep it
     * in step with TwoPaneModal's sheet padding, as the footer's `-mb-6` is.
     */
    pinned?: boolean;
    className?: string;
}

const TILE = 'size-14 shrink-0 overflow-hidden rounded-lg';

/**
 * Names the thing the gate is about, for the surface that cannot show it any
 * other way.
 *
 * The desktop card carries this in its brand pane: the item's own photograph
 * with a statement over it, which is most of what tells the reader they are
 * enquiring about *this* item from *this* professional. The sheet drops that
 * pane — a phone has no room for imagery beside the thing the reader came to do
 * — and without a stand-in the form opens with no subject at all, which reads
 * as a generic contact form rather than as the enquiry they started.
 *
 * Both marks, as the legacy header had them: the professional's logo says who
 * receives this, the item photograph says what it is about. Either alone leaves
 * half the question open.
 *
 * Rendered by the gate rather than by the shell, because only the gate knows
 * what its subject is.
 */
export const GateSubjectHeader = ({
    image,
    logo,
    logoBackground,
    title,
    professionalTitle,
    logoPending = false,
    pinned = false,
    className,
}: GateSubjectHeaderProps) => {
    const showLogo = Boolean(logo) || logoPending;

    return (
        <div
            className={cn(
                'flex items-center gap-3',
                pinned &&
                    'sticky top-0 z-10 -mx-6 border-b border-black/5 bg-white px-6 pt-2 pb-3',
                className,
            )}
        >
            {showLogo || image ? (
                <div className="flex shrink-0 items-center gap-2">
                    {showLogo ? (
                        <div
                            className={cn(
                                TILE,
                                'flex items-center justify-center bg-[#f3f3f3]',
                            )}
                            style={
                                logoBackground
                                    ? { backgroundColor: logoBackground }
                                    : undefined
                            }
                        >
                            {logo ? (
                                <img
                                    className="h-full w-full object-contain"
                                    src={logo}
                                    alt=""
                                    loading="eager"
                                />
                            ) : (
                                <Skeleton className="h-full w-full" />
                            )}
                        </div>
                    ) : null}
                    {image ? (
                        <div className={cn(TILE, 'bg-[#f3f3f3]')}>
                            <Image
                                className="h-full w-full object-cover"
                                src={image}
                                alt=""
                                width={56}
                                height={56}
                                fit="cover"
                                fadeIn={false}
                                loading="eager"
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
            <div className="min-w-0">
                {/* The item is the subject; the professional is who it reaches.
                    A professional-level enquiry has no item, so they swap and
                    the header still names something. */}
                <p className="line-clamp-2 text-[1.0625rem] leading-tight font-medium tracking-[-0.02em] text-[#181818]">
                    {title || professionalTitle}
                </p>
                {title ? (
                    <p className="mt-1 truncate text-[0.9375rem] tracking-[-0.01em] text-[#828282]">
                        {professionalTitle}
                    </p>
                ) : null}
            </div>
        </div>
    );
};
